import { FONT_FAMILIES_LIST } from "../constants/canvasConstants";

/**
 * Checks if a font is loaded and available in the browser
 * @param fontFamily - The font family to check
 * @returns Promise that resolves when the font is loaded
 */
export const waitForFontToLoad = (fontFamily: string): Promise<void> => {
  return new Promise((resolve) => {
    if (document.fonts && document.fonts.check) {
      // Use the Font Loading API if available
      const font = `12px "${fontFamily}"`;

      if (document.fonts.check(font)) {
        resolve();
        return;
      }

      // Wait for font to load
      document.fonts
        .load(font)
        .then(() => {
          resolve();
        })
        .catch(() => {
          // Font failed to load, resolve anyway to prevent hanging
          resolve();
        });
    } else {
      // Fallback for older browsers
      const testString = "abcdefghijklmnopqrstuvwxyz";
      const testDiv = document.createElement("div");
      testDiv.style.fontFamily = fontFamily;
      testDiv.style.fontSize = "12px";
      testDiv.style.position = "absolute";
      testDiv.style.left = "-9999px";
      testDiv.style.top = "-9999px";
      testDiv.style.visibility = "hidden";
      testDiv.textContent = testString;

      document.body.appendChild(testDiv);

      // Check if font is different from default
      const defaultWidth = testDiv.offsetWidth;
      testDiv.style.fontFamily = "Arial";
      const arialWidth = testDiv.offsetWidth;

      document.body.removeChild(testDiv);

      if (defaultWidth !== arialWidth) {
        resolve();
      } else {
        // Wait a bit and resolve anyway
        setTimeout(() => resolve(), 100);
      }
    }
  });
};

/**
 * Preloads all Google Fonts used in the application
 * @returns Promise that resolves when all fonts are loaded
 */
export const preloadAllFonts = (): Promise<void[]> => {
  const systemFonts = ["Times New Roman", "Arial", "Verdana", "Georgia", "Comic Sans MS", "Impact"];
  const googleFonts = FONT_FAMILIES_LIST.filter((font) => !systemFonts.includes(font));

  return Promise.all(googleFonts.map((font) => waitForFontToLoad(font)));
};

/**
 * Checks if a font family is a Google Font
 * @param fontFamily - The font family to check
 * @returns true if it's a Google Font
 */
export const isGoogleFont = (fontFamily: string): boolean => {
  const systemFonts = ["Times New Roman", "Arial", "Verdana", "Georgia", "Comic Sans MS", "Impact"];
  return FONT_FAMILIES_LIST.includes(fontFamily as any) && !systemFonts.includes(fontFamily);
};

/**
 * Ensures a font is loaded before using it in Fabric.js
 * @param fontFamily - The font family to ensure is loaded
 * @returns Promise that resolves when the font is ready
 */
export const ensureFontLoaded = async (fontFamily: string): Promise<void> => {
  if (isGoogleFont(fontFamily)) {
    await waitForFontToLoad(fontFamily);
  }
  // System fonts are always available, no need to wait
};
